import React, { useState, useEffect, useRef, useId } from 'react'
import RichText from '/react4xp/components/RichText'

interface Props {
   title: string,
   intro: string,
   faq: {
      question: string,
      answer: string,
   }[],
   headingLevel?: 2 | 3 | 4 | 5 | 6,
}

const chevron = (
   <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M19.5 9L12 16.5L4.5 9" stroke="#002855" strokeWidth="2" strokeLinecap="square" />
   </svg>
)

export default function FAQ(props: Props) {
   const { title, intro, faq, headingLevel = 2 } = props
   const instanceId = useId()

   // a freely-placed part cannot know its own depth, so the level is set where
   // the page is composed; 2 is right wherever the page title is the only h1
   const Heading = `h${headingLevel}` as const
   // each question is a section under the FAQ title, so one level below it
   const QuestionHeading = `h${headingLevel + 1}` as 'h3' | 'h4' | 'h5' | 'h6'
   const [openIndex, setOpenIndex] = useState<number | null>(null)
   const contentRefs = useRef<(HTMLDivElement | null)[]>([])
   const [currentContentHeight, setCurrentContentHeight] = useState<number>(0)

   const toggleAccordion = (index: number) => {
      setOpenIndex(openIndex === index ? null : index)
   }

   const updateContentHeight = () => {
      if (openIndex !== null) {
         const contentHeight = contentRefs.current[openIndex]?.getBoundingClientRect().height || 0
         setCurrentContentHeight(contentHeight)
      }
   }

   const handleResize = () => {
      updateContentHeight()
   }

   useEffect(() => {
      if (openIndex !== null) {
         updateContentHeight()
      }
   }, [openIndex])

   useEffect(() => {
      window.addEventListener('resize', handleResize)
      return () => {
         window.removeEventListener('resize', handleResize)
      }
   }, [openIndex])

   return (
      <div className="faq">
         <div className="grid-container">
            <div className="grid-row faq__wrapper">
               <div className="c-sm-4">
                  <div className="faq__aside">
                     {title && <Heading className="title font-title">{title}</Heading>}
                     {intro && <div className="intro"><RichText content={intro} /></div>}
                  </div>
               </div>
               <div className="c-sm-8">
                  <div className="faq__accordion">
                     {faq.map((item, index) => {
                        const isOpen = openIndex === index
                        const questionId = `${instanceId}-question-${index}`
                        const answerId = `${instanceId}-answer-${index}`

                        return (
                           <div
                              className={`faq__item ${isOpen ? 'faq__item--open' : ''}`}
                              key={index}
                           >

                              <QuestionHeading className="faq__question">
                                 <button
                                    type="button"
                                    id={questionId}
                                    aria-expanded={isOpen}
                                    aria-controls={answerId}
                                    onClick={() => toggleAccordion(index)}
                                 >
                                    <span className="text" data-text={item.question || 'Question'}>
                                       <span>{item.question || 'Question'}</span>
                                    </span>
                                    <span className="icon">{chevron}</span>
                                 </button>
                              </QuestionHeading>

                              <div
                                 className="wrapper"
                                 id={answerId}
                                 role="region"
                                 aria-labelledby={questionId}
                                 style={{
                                    maxHeight: isOpen ? `${currentContentHeight}px` : '0px'
                                 }}
                              >
                                 <div ref={(el) => { contentRefs.current[index] = el }} className="content">
                                    {item.answer && <RichText content={item.answer} />}
                                 </div>
                              </div>
                           </div>
                        )
                     })}
                  </div>
               </div>
            </div>
         </div>
      </div>
   )
}
